import { ApiClientError, staleDomainRefusal } from '@/lib/api/client'
import { discardRefusedDomain, type DomainScopedModule } from '@/lib/tenant/domain-selection'

/**
 * One ER or ESA request that survives a stale selected domain. One rule for both modules: two copies of the domain
 * rules is how ER kept a bug ESA had already fixed for three weeks (HFV3-BACKEND-P).
 *
 * The choice lives in localStorage and outlives what it named: a domain deleted, or chosen in another workspace or
 * company, is still there at the next visit, and the provider honours a stored id until the domain list arrives. So
 * the page's first requests can name a domain the API refuses ('Invalid tenant context'). The domain is forgotten
 * and a read is asked again, on what the selection is by then. A write is never repeated: the caller gets the
 * refusal, and its next try carries the right domain.
 *
 * If the workspace itself is the one that is gone, the second request says so without a domain on it, and
 * fetchApi's H7 handles it as it always did.
 */
export async function withStaleDomainRecovery<T>(
  module: DomainScopedModule,
  method: string | undefined,
  buildHeaders: () => Record<string, string>,
  send: (headers: Record<string, string>) => Promise<T>,
): Promise<T> {
  const headers = buildHeaders()
  try {
    return await send(headers)
  } catch (err) {
    const refused = headers['x-domain-id']
    if (!refused || !(err instanceof ApiClientError) || !staleDomainRefusal(err.statusCode, err.body)) throw err
    discardRefusedDomain(module, refused)
    if ((method ?? 'GET').toUpperCase() !== 'GET') throw err
    const again = buildHeaders()
    // A domain the caller named itself comes back from buildHeaders: it is the one just refused.
    if (again['x-domain-id'] === refused) delete again['x-domain-id']
    return send(again)
  }
}
